import { useApolloClient } from '@apollo/client/react/compiled'
import { commonMessages } from '@common/intl'
import { useApolloHandler } from '@dolpheen/apollo/ApolloProvider'
import type { UserFragment } from '@gql'
import { TokenAuthDocument, VerifyTokenDocument } from '@gql'
import { useMutation } from '@hooks/graphql'
import useAppState from '@hooks/useAppState'
import useNotifier from '@hooks/useNotifier'
import { useUserNotifications } from '@hooks/useUserNotifications'
import type { IUserContext } from '@sections/auth/context'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import {
  isSupported as isCredentialsManagementAPISupported,
  login as loginWithCredentialsManagementAPI,
  saveCredentials,
} from '../credentialsManagement'
import { getTokens, removeTokens, setTokens } from '../utils'

export const shouldPersistToken = true

export function useAuthProvider(): IUserContext {
  const [user, setUserInternal] = useState<UserFragment | undefined | null>(undefined)
  const [onLogIn, setOnLogIn] = useState<(() => void) | undefined>(undefined)
  const isOnLogin = !!onLogIn
  const isAuthenticated = !!user
  const permitCredentialsAPI = useRef(true)
  const verifyingToken = useRef(false)

  const client = useApolloClient()

  const [, dispatchAppState] = useAppState()

  const setUser = useCallback((_user: UserFragment | null) => {
    setUserInternal(_user)
  }, [])

  useEffect(() => {
    if (isAuthenticated) {
      permitCredentialsAPI.current = true
    }
  }, [isAuthenticated])

  const [tokenAuth, tokenAuthResult] = useMutation(TokenAuthDocument, {
    onError: () => {
      logout()
      setUser(null)
    },
  })

  const [tokenVerify, tokenVerifyResult] = useMutation(VerifyTokenDocument, {
    context: {
      tokenInVariable: 'token',
    },
    onCompleted: (result) => {
      if (result.tokenVerifyUser === null) {
        logout()
      } else {
        const user = result.tokenVerifyUser?.user

        if (!!user) {
          setUser(user)
        }
      }
    },
    onError: () => {
      dispatchAppState({
        payload: {
          error: 'unhandled',
        },
        type: 'displayError',
      })
    },
  })

  const logout = useCallback(() => {
    setUser(null)
    if (isCredentialsManagementAPISupported) {
      navigator.credentials.preventSilentAccess()
    }

    void client.clearStore()
    removeTokens()
  }, [client, setUser])

  const notify = useNotifier()
  const intl = useIntl()

  const handleAuthError = useCallback(() => {
    logout()
    notify({
      status: 'error',
      text: intl.formatMessage(commonMessages.sessionExpired),
    })
  }, [logout, notify, intl])
  useApolloHandler('onAuthError', handleAuthError)

  const login = useCallback(
    async (email: string, password: string): Promise<unknown> =>
      tokenAuth({
        onCompleted: (data) => {
          const user = data.tokenCreate.user

          setUser(user)
          setTokens(data.tokenCreate.accessToken, data.tokenCreate.refreshToken, shouldPersistToken)

          saveCredentials(data.tokenCreate.user, password)
          onLogIn?.()
        },
        variables: { email, password },
      }),
    [onLogIn, tokenAuth, setUser],
  )

  const loginByToken = (auth: string, refresh: string, user: UserFragment) => {
    setUser(user)
    setTokens(auth, refresh, shouldPersistToken)
  }

  useEffect(() => {
    if (!verifyingToken.current) {
      const token = getTokens().auth
      if (!!token && !user) {
        verifyingToken.current = true
        tokenVerify({ variables: { token } }).finally(() => {
          verifyingToken.current = false
        })
      }
    }
  }, [tokenVerify, user])

  useEffect(() => {
    const token = getTokens().auth
    if (!(!!token && !user) && isOnLogin && permitCredentialsAPI.current) {
      permitCredentialsAPI.current = false
      loginWithCredentialsManagementAPI(login)
    }
  }, [isOnLogin, login, user])

  useUserNotifications(
    !isAuthenticated ||
      tokenAuthResult.loading ||
      tokenVerifyResult.loading ||
      !!tokenAuthResult.error ||
      !!tokenVerifyResult.error,
  )

  return {
    isOnLogin,
    isTokenAuthLoading: tokenAuthResult.loading,
    isTokenVerifyLoading: tokenVerifyResult.loading,
    login,
    loginByToken,
    logout,
    setOnLogIn,
    tokenAuthError: tokenAuthResult.error,
    tokenVerifyError: tokenVerifyResult.error,
    user,
  }
}
